'use client';

import { getSessionId } from '@repo/common-utils';
import { useUserEventLogger } from '@repo/tracking-service';
import { MainTitle, TextButton } from '@repo/ui';
import { useSetAtom } from 'jotai';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';

import s from '@/app/_components/chat-content/MoreQuestion.module.scss';
import { TailBubble } from '@/app/_components/chat-content/TextBubble';
import { useChatMessage } from '@/lib/hooks/use-chat-message';
import { useSenaWebDb } from '@/lib/hooks/use-sena-web-db';
import { currentQuestionAtom, loadedChatContentAtom } from '@/lib/stores/chat';
import { ChatItemContent, QuestionContent } from '@/lib/types/content';
import { GradientBubbleColor } from '@/lib/types/content';

export const RelatedQuestion = ({
  content,
  titleSize,
  clickAction
}: {
  content: ChatItemContent;
  titleSize?: 'large' | 'medium' | 'small' | 'xsmall';
  clickAction?: (value: string) => void;
}) => {
  const setLoadedChatContent = useSetAtom(loadedChatContentAtom);
  const setCurrentQuestion = useSetAtom(currentQuestionAtom);
  const router = useRouter();
  const pathname = usePathname();
  const bubbleColors: Array<GradientBubbleColor> = ['orange', 'green', 'blue', 'olive'];
  const maxWidths: Array<string> = ['82%', '90%', '75%', '82%'];
  const senaWebDb = useSenaWebDb();
  const { generateNewChatId, sendMessage } = useChatMessage();
  const { sendClickEvent } = useUserEventLogger();

  const startNewChat = (value: string) => {
    if (pathname === '/chat') {
      sendMessage(value);
    } else {
      const sessionId = getSessionId();
      const chatId = generateNewChatId();
      const deliveryContent: ChatItemContent[] = [
        { id: content.id, question: content.question, answer: { ...content.answer, type: 'content' }, isComplete: true, isDirectRequest: false }
      ];
      setCurrentQuestion(value);
      setLoadedChatContent(deliveryContent);
      senaWebDb.setChatContent(sessionId, chatId, deliveryContent).then(() => router.push('/chat'));
    }
  };

  return (
    <div>
      <MainTitle title={'관련 질문들을 찾아봤어요'} size={titleSize ?? 'small'} className={'pt-spacious-small'} />
      <div className={'grid grid-cols-1 gap-normal-small'}>
        {content.answer.relatedQuestions?.map((value, index) => (
          <TailBubble
            key={index}
            text={value}
            maxWidth={maxWidths[index] ?? '100%'}
            color={bubbleColors[index % bubbleColors.length] ?? 'basic'}
            clickAction={() => {
              sendClickEvent('question_related', { index: index + 1, 'origin-content-id': content.id });
              if (!clickAction) {
                startNewChat(value);
                return;
              }

              clickAction(value);
            }}
          />
        ))}
      </div>
    </div>
  );
};

export const RecentQuestion = ({ content }: { content: QuestionContent }) => {
  const { sendClickEvent } = useUserEventLogger();
  const router = useRouter();

  return (
    <div>
      <MainTitle title={'이런 질문은 궁금하지 않으세요?'} size={'small'} className={'pt-spacious-medium'} />
      <div className={s.recent_cards_scroll_area}>
        <div className={s.recent_cards}>
          {content.recentContents?.slice(0, 3)?.map((item, index) => (
            <Link
              key={item.id}
              href={`/contents/${item.id}`}
              className={s.recent_card}
              onClick={() => {
                sendClickEvent('question_recent', { index: index + 1, 'content-id': item.id, 'origin-content-id': content.id });
              }}
            >
              <p className={`${s.recent_card_text} !m-0`}>{item.question}</p>
            </Link>
          ))}
          <TextButton
            variant={'secondary'}
            size={'medium'}
            className={`${s.recent_card_more_button} !rounded-bzc-large`}
            onClick={() => {
              sendClickEvent('question_recent_more');
              router.push('/');
            }}
          >
            <span className={s.recent_card_more_button_text}>
              <span>→</span>
              <span>더보기</span>
            </span>
          </TextButton>
        </div>
      </div>
    </div>
  );
};
